/**
 * 平台的界面示意。
 *
 * 这些不是截图，而是用真实字段手绘的等价物：机体编号与端口取自本地模拟机队
 * （9201–9206），指标名与等级取自平台的遥测与告警模型。用 HTML + SVG 画而不是贴图，
 * 是为了让它跟随深浅色主题、并且在高分屏上保持锐利。
 */
import { Icon } from '@/components/Icon';

interface FleetRow {
  name: string;
  port: string;
  battery: number;
  link: '良好' | '一般' | '稍弱';
  note: string;
  level: 'ok' | 'caution' | 'warning';
}

const FLEET_ROWS: readonly FleetRow[] = [
  { name: 'drone-01', port: '9201', battery: 78, link: '良好', note: '巡航高度 82 m', level: 'ok' },
  { name: 'drone-02', port: '9202', battery: 64, link: '良好', note: '返航中', level: 'ok' },
  { name: 'drone-03', port: '9203', battery: 34, link: '一般', note: '电量注意', level: 'caution' },
  { name: 'drone-04', port: '9204', battery: 22, link: '稍弱', note: '电量警告', level: 'warning' },
  { name: 'drone-05', port: '9205', battery: 91, link: '良好', note: '待命', level: 'ok' },
  { name: 'drone-06', port: '9206', battery: 55, link: '一般', note: '链路一般', level: 'caution' },
];

const LEVEL_STYLE: Record<FleetRow['level'], { dot: string; text: string }> = {
  ok: { dot: 'bg-palette-3', text: 'text-fg-muted' },
  caution: { dot: 'bg-palette-4', text: 'text-fg' },
  warning: { dot: 'bg-palette-5', text: 'text-fg' },
};

/** 机队总览：一屏看完在场机体的电量与链路。 */
export function FleetOverviewMock({ className }: { className?: string }) {
  return (
    <div className={['card-surface overflow-hidden', className].filter(Boolean).join(' ')}>
      <div className="flex items-center justify-between gap-3 border-b border-line bg-surface-2 px-4 py-3">
        <div className="flex items-center gap-2">
          <Icon name="fleet" size={16} />
          <span className="text-sm font-medium text-fg">机队总览</span>
        </div>
        <span className="metric-value flex items-center gap-1.5 text-[0.6875rem] text-fg-muted">
          <span className="h-1.5 w-1.5 rounded-full bg-palette-3" />
          6 / 6 在线
        </span>
      </div>

      <ul className="divide-y divide-line">
        {FLEET_ROWS.map((row) => {
          const style = LEVEL_STYLE[row.level];
          return (
            <li key={row.name} className="flex items-center gap-3 px-4 py-2.5">
              <span className={`h-1.5 w-1.5 shrink-0 rounded-full ${style.dot}`} />
              <span className="metric-value w-20 shrink-0 text-[0.8125rem] text-fg">{row.name}</span>
              <span className="metric-value hidden w-11 shrink-0 text-[0.6875rem] text-fg-subtle sm:block">
                :{row.port}
              </span>
              <span className="flex min-w-0 flex-1 items-center gap-2">
                <span className="h-1 w-16 shrink-0 overflow-hidden rounded-full bg-surface-2">
                  <span className="block h-full rounded-full bg-palette-4" style={{ width: `${row.battery}%` }} />
                </span>
                <span className="metric-value text-[0.6875rem] text-fg-muted">{row.battery}%</span>
              </span>
              <span className={`hidden truncate text-[0.6875rem] sm:block ${style.text}`}>{row.note}</span>
            </li>
          );
        })}
      </ul>

      <div className="flex items-center justify-between gap-3 border-t border-line bg-surface-2 px-4 py-2.5 text-[0.6875rem] text-fg-muted">
        <span className="metric-value">遥测 1 Hz · 断链判定 3 s</span>
        <span>数据来自本地模拟机队</span>
      </div>
    </div>
  );
}

/** 姿态球：地平线、俯仰刻度与机体朝向。 */
export function AttitudeBall({ size = 132, className }: { size?: number; className?: string }) {
  return (
    <svg
      viewBox="0 0 120 120"
      width={size}
      height={size}
      className={className}
      role="img"
      aria-label="机体姿态示意：接近水平"
      focusable="false"
    >
      <circle cx="60" cy="60" r="54" className="fill-surface-2" />
      <circle cx="60" cy="60" r="54" fill="none" className="stroke-line-strong" strokeWidth="1.5" />
      <clipPath id="attitude-clip">
        <circle cx="60" cy="60" r="53" />
      </clipPath>
      <g clipPath="url(#attitude-clip)">
        <rect x="0" y="0" width="120" height="66" className="fill-surface-3" opacity="0.55" />
        <path d="M0 66h120" className="stroke-line-strong" strokeWidth="1.5" />
        <g className="stroke-fg-subtle" strokeWidth="0.8" opacity="0.7">
          <path d="M18 50h14" />
          <path d="M88 50h14" />
          <path d="M18 26h14" />
          <path d="M88 26h14" />
        </g>
      </g>
      <g className="stroke-fg" strokeWidth="2" strokeLinecap="round" fill="none">
        <path d="M36 60h20" />
        <path d="M64 60h20" />
        <path d="M56 60h8" opacity="0.5" />
      </g>
      <circle cx="60" cy="60" r="2.4" className="fill-fg" />
      <g className="stroke-fg-muted" strokeWidth="1.2" opacity="0.6">
        <path d="M60 4v6" />
        <path d="M60 110v6" />
        <path d="M4 60h6" />
        <path d="M110 60h6" />
      </g>
    </svg>
  );
}

/** 单机管理页的核心读数：四个大数字 + 姿态球。 */
export function SingleDroneMock({ className }: { className?: string }) {
  const metrics = [
    { label: '相对高度', value: '82.4', unit: 'm' },
    { label: '地速', value: '6.2', unit: 'm/s' },
    { label: '电量', value: '78', unit: '%' },
    { label: '卫星数', value: '14', unit: '颗' },
  ];

  return (
    <div className={['card-surface p-4', className].filter(Boolean).join(' ')}>
      <div className="mb-4 flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Icon name="tracking" size={16} />
          <span className="metric-value text-sm text-fg">drone-01</span>
        </div>
        <span className="inline-flex items-center gap-1.5 rounded-md bg-grad-tint px-2.5 py-1 text-[0.6875rem] font-medium text-brand">
          <span className="h-1.5 w-1.5 rounded-full bg-palette-3" />
          链路良好
        </span>
      </div>

      <div className="flex flex-col items-center gap-5 sm:flex-row">
        <AttitudeBall size={124} className="shrink-0" />
        <div className="grid w-full grid-cols-2 gap-x-4 gap-y-3">
          {metrics.map((metric) => (
            <div key={metric.label} className="flex flex-col gap-0.5">
              <span className="text-[0.6875rem] text-fg-muted">{metric.label}</span>
              <span className="flex items-baseline gap-1">
                <span className="metric-value text-2xl text-fg">{metric.value}</span>
                <span className="metric-value text-[0.6875rem] text-fg-subtle">{metric.unit}</span>
              </span>
            </div>
          ))}
        </div>
      </div>

      <div className="mt-4 flex flex-wrap gap-2 border-t border-line pt-3">
        <span className="inline-flex items-center gap-1.5 rounded-lg border border-line px-2.5 py-1.5 text-[0.6875rem] text-fg">
          <Icon name="zap" size={13} />
          起飞
        </span>
        <span className="inline-flex items-center gap-1.5 rounded-lg border border-line px-2.5 py-1.5 text-[0.6875rem] text-fg">
          <Icon name="route" size={13} />
          返航
        </span>
        <span className="inline-flex items-center gap-1.5 rounded-lg border border-line px-2.5 py-1.5 text-[0.6875rem] text-fg-subtle">
          <Icon name="gauge" size={13} />
          降落
        </span>
      </div>
    </div>
  );
}
